import { type MutableDatabase } from '@databases/model/database';
import { type MutableView } from '@databases/model/views/view';
import {
  type DatabaseCardWidth,
  type DatabaseFilter,
  DatabaseLayoutType,
  type DatabaseView,
} from '@databases/types/database/view';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { DatabaseChangeType } from '@databases/types/redux';
import { type DatabaseChange } from '@databases/utils/database/changesets';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { addChangeToYjs } from '@databases/utils/redux/redux-to-yjs/add-change-to-yjs';

export interface LayoutSettingsOptions {
  layoutType?: DatabaseLayoutType;
  groupFieldId?: string;
  numberedEntries?: boolean;
  cardWidth?: DatabaseCardWidth;
  showFieldNames?: boolean;
}

export class ViewAgent {
  private database: MutableDatabase;

  constructor(database: MutableDatabase) {
    this.database = database;
  }

  public addView(view: DatabaseView): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.ADD_SAVED_VIEW,
        view,
      },
      undefined
    );
  }

  public deleteView(viewId: string): void {
    this.database.mutate((db) => {
      db.deleteView(viewId);
    });
  }

  public isViewsEmpty(): boolean {
    return this.database.views.length === 0;
  }

  public getViewCount(): number {
    return this.database.views.length;
  }

  public setDefaultView(viewId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_DEFAULT_SAVED_VIEW,
        viewId,
      },
      undefined
    );
  }

  public updateViewLayout(viewId: string, settings: LayoutSettingsOptions): void {
    const changes: DatabaseChange[] = [];
    if (settings.layoutType) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE,
        viewId,
        newLayoutType: settings.layoutType,
      });
    }
    if (settings.groupFieldId) {
      changes.push({
        type: DatabaseChangeType.SET_GROUPING_FIELD,
        viewId,
        fieldId: settings.groupFieldId,
        manualGroupOrder: [],
      });
    }
    if (settings.numberedEntries !== undefined) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES,
        viewId,
        newShowNumberedEntries: settings.numberedEntries,
      });
    }
    if (settings.cardWidth) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH,
        viewId,
        newCardWidth: settings.cardWidth,
      });
    }
    if (settings.showFieldNames !== undefined) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES,
        viewId,
        newShowFieldNames: settings.showFieldNames,
      });
    }
    addChangeToYjs(
      this.database,
      { type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES, changes },
      undefined
    );
  }

  public addSortToView(viewId: string, fieldId: string, ascending: boolean): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.APPEND_SAVED_VIEW_SORT,
            viewId,
            sort: {
              fieldId,
              ascending,
            },
          },
        ],
      },
      undefined
    );
  }

  public updateSort(viewId: string, fieldId: string, ascending: boolean): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE,
            viewId,
            fieldId,
            ascending,
          },
        ],
      },
      undefined
    );
  }

  public removeSort(viewId: string, fieldId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.DELETE_SAVED_VIEW_SORT,
            viewId,
            fieldId,
          },
        ],
      },
      undefined
    );
  }

  public hasSortForField(viewId: string, fieldId: string): boolean {
    const view = this.database.viewsById[viewId];
    if (!view) {
      return false;
    }
    return view.sorts.toArray().some((sort) => sort.fieldId === fieldId);
  }

  public setFilter(viewId: string, filter: DatabaseFilter): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.SET_SAVED_VIEW_FILTER,
            viewId,
            newFilter: filter,
            path: [],
          },
        ],
      },
      undefined
    );
  }

  public clearViewFilter(viewId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES,
        changes: [
          {
            type: DatabaseChangeType.CLEAR_SAVED_VIEW_FILTER,
            viewId,
          },
        ],
      },
      undefined
    );
  }

  public getViewIdByIndex(index: number): string | undefined {
    return this.database.views[index];
  }

  public getViewIndexById(viewId: string): number {
    return this.database.views.indexOf(viewId);
  }

  public getViewIdByName(name: string): string | undefined {
    for (const [viewId, view] of Object.entries(this.database.viewsById)) {
      if (view.name === name) {
        return viewId;
      }
    }
  }

  public getViewById(viewId: string): MutableView | undefined {
    return this.database.viewsById[viewId];
  }

  public getViewLayoutType(viewId: string): DatabaseLayoutType | undefined {
    const view = this.database.viewsById[viewId];
    if (!view) {
      return undefined;
    }
    const layoutType = view.layoutType;
    if (layoutType === 'table') {
      return DatabaseLayoutType.TABLE;
    }
    if (layoutType === 'board') {
      return DatabaseLayoutType.BOARD;
    }
    if (layoutType === 'gallery') {
      return DatabaseLayoutType.GALLERY;
    }
    if (layoutType === 'calendar') {
      return DatabaseLayoutType.CALENDAR;
    }
    return undefined;
  }

  public updateViewName(viewName: string, newName: string): void {
    const viewId = this.getViewIdByName(viewName);

    if (!viewId) {
      throw new Error(`View with name ${viewName} not found`);
    }

    const existingViewId = this.getViewIdByName(newName);
    if (existingViewId) {
      throw new Error(`View with name ${newName} already exists`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_NAME,
        viewId,
        newName,
        delta: [
          { delete: viewName.length },
          {
            insert: newName,
          },
        ],
      },
      undefined
    );
  }

  public reorderView(viewId: string, newIndex: number): void {
    const currentIndex = this.getViewIndexById(viewId);
    const adjustedIndex = newIndex > currentIndex ? newIndex + 1 : newIndex;

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.REORDER_SAVED_VIEW,
        viewId,
        newIndex: adjustedIndex,
      },
      undefined
    );
  }

  public hideFields(viewId: string, fieldIds: string[]): void {
    const changes: DatabaseChange[] = [];
    for (const fieldId of fieldIds) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS,
        viewId,
        fieldKey: fieldId,
        fieldValue: false,
      });
    }

    if (changes.length > 0) {
      addChangeToYjs(
        this.database,
        { type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES, changes },
        undefined
      );
    }
  }

  public showFields(viewId: string, fieldIds: string[]): void {
    const changes: DatabaseChange[] = [];
    for (const fieldId of fieldIds) {
      changes.push({
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS,
        viewId,
        fieldKey: fieldId,
        fieldValue: true,
      });
    }

    if (changes.length > 0) {
      addChangeToYjs(
        this.database,
        { type: DatabaseChangeType.BULK_APPLY_SAVED_VIEW_CHANGES, changes },
        undefined
      );
    }
  }
}
